import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Textarea } from "./ui/textarea"
import { Switch } from "./ui/switch"
import { Checkbox } from "./ui/checkbox"
import { ArrowLeft, Save } from "lucide-react"
import { useUserRol, useTienePermiso } from '../App';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select"
import { RechazoModal } from "./RechazoModal";
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService';

interface NMXA4920FormData {
  fecha: string
  hora: string
  gradosC: string
  porcentajeHR: string
  temp20C: boolean
  temp27C: boolean
  
  // Grado de humedecimiento (3 muestras)
  humed1: string
  humed2: string
  humed3: string
  
  observaciones: string
  mostrarObservaciones: boolean
  realizo: string
  superviso: string
  numero_solicitud?: string
  id_termohigrometro?: string
  desv_metodo?: string;
}

interface NMXA4920FormProps {
  onBack: () => void
  pedidoId: string
  pedidoRowId?: string
  mode: 'create' | 'view' | 'edit'
  initialData?: Partial<NMXA4920FormData>
  realizoUser?: string
  rol?: string // <-- NUEVO: rol del usuario
  onSave?: (data: NMXA4920FormData) => void // Agregado para evitar error de referencia
  onRefreshBackend?: () => void;
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXA4920Form({ onBack, pedidoId, pedidoRowId, mode, initialData = {}, realizoUser, rol, onSave, onRefreshBackend, onAutosave }: NMXA4920FormProps) {
  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol;

  const [formData, setFormData] = useState<NMXA4920FormData>({
    fecha: "",
    hora: "",
    gradosC: "",
    porcentajeHR: "",
    temp20C: false,
    temp27C: false,
    humed1: "",
    humed2: "",
    humed3: "",
    observaciones: "",
    superviso: "",
    desv_metodo: initialData?.desv_metodo || "",
    ...initialData,
    realizo: mode === 'create' && realizoUser ? realizoUser : (initialData?.realizo || ""),
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  const [editMode, setEditMode] = useState(false)
  const [estatus, setEstatus] = useState<string>(initialData && (initialData as any).ACEPTADO ? (initialData as any).ACEPTADO : '')
  const isAceptado = estatus === 'aceptado' || estatus === 'rechazado'

  // Estado para modal de rechazo y loading
  const [showRechazoModal, setShowRechazoModal] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  // --- Autosave (borrador) ---
  const [autosaveRowId, setAutosaveRowId] = useState<string>((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '');
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;

  // Set realizo from prop always in create mode
  useEffect(() => {
    if (mode === 'create' && realizoUser && formData.realizo !== realizoUser) {
      setFormData(prev => ({ ...prev, realizo: realizoUser }))
    }
    // eslint-disable-next-line
  }, [realizoUser, mode])

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData(prev => ({ ...prev, ...initialData, mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true }))
    // Sincroniza estatus local con initialData.ACEPTADO o initialData.ACEPTAR
    setEstatus((initialData as any)?.ACEPTADO || '')
    setEditMode(false)
    setAutosaveRowId((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '')
    setAutosaveNomId((initialData as any)?.nomId || '')
  }, [initialData])

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  const getSupervisorName = () => {
    // Prioridad: realizoUser (prop), luego globalRol, luego vacío
    return realizoUser || globalRol || '';
  }

  const handleInputChange = (field: keyof NMXA4920FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleTemperatureChange = (tempField: 'temp20C' | 'temp27C', checked: boolean) => {
    if (checked) {
      // Si se marca uno, desmarcar el otro
      setFormData(prev => ({
        ...prev,
        temp20C: tempField === 'temp20C',
        temp27C: tempField === 'temp27C'
      }))
    } else {
      // Si se desmarca, solo desmarcar ese
      setFormData(prev => ({
        ...prev,
        [tempField]: false
      }))
    }
  }

  const formPayload = () => formData

  const [guardando, setGuardando] = useState(false)

  // handleSave ahora solo llama a onSave, igual que en AnalisisQuimicoForm
  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) {
        onSave(formPayload())
      }
    } finally {
      setGuardando(false)
    }
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-4920-INNTEX-2017 RESISTENCIA AL HUMEDECIMIENTO SUPERFICIAL (MÉTODO DE ROCÍO)',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const confirmMsg = accion === 'aceptado'
      ? '¿Estás seguro de que quieres aceptar la prueba NMX-A-4920? Esta acción no podrá editarse ni borrarse.'
      : '¿Estás seguro de que quieres rechazar la prueba NMX-A-4920? Esta acción no podrá editarse ni borrarse.';
    if (!window.confirm(confirmMsg)) return;
    const payload: any = {
      ROW_ID: (initialData as any)?.ROW_ID || (initialData as any)?.row_id || '',
      PEDIDO: pedidoId,
      ...formData,
      SUPERVISO: getSupervisorName(),
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion
    };
    if (accion === 'rechazado' && razonRechazo) {
      payload.RAZON_RECHAZO = razonRechazo;
    }
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '', accion, accion === 'rechazado' ? payload.RAZON_RECHAZO : undefined, undefined, (initialData as any)?.nomId || '');
      setEstatus(payload.ACEPTAR)
      setEditMode(false)
      setShowRechazoModal(false)
      if (onRefreshBackend) onRefreshBackend();
      alert(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} y enviada al backend.`)
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err))
    } finally {
      if (accion === 'rechazado') setRechazoLoading(false);
    }
  }

  const handleEditar = () => {
    setEditMode(true)
  }

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-4920? Esta acción no se puede deshacer.')) return;
    const payload = {
      ROW_ID: (initialData as any)?.ROW_ID || (initialData as any)?.row_id || '',
      PEDIDO: pedidoId,
      ...formData,
      SUPERVISO: getSupervisorName(), // <-- Se asigna automáticamente
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado' // <-- Agregado para que se envíe correctamente
    }
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  }

  const isReadOnly = mode === 'create' ? false : (mode === 'view' && !editMode)

  const HUMED_OPTIONS = [
    "0 – Humedecimiento de la totalidad de la cara del espécimen.",
    "1 – Humedecimiento completo de toda la cara del espécimen más allá de los puntos de rociado.",
    "2 – Humedecimiento parcial de la cara del espécimen más allá de los puntos de rociado.",
    "3 – Humedecimiento de la cara del espécimen en los puntos de rociado.",
    "4 – Ligero humedecimiento aleatorio, de la cara del espécimen.",
    "5 – Sin humedecimiento de la cara del espécimen."
  ];

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handleBackFlush}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                NMX-A-4920-INNTEX-2017
              </h1>
              <p className="text-muted-foreground">
                DETERMINACIÓN DE LA RESISTENCIA AL HUMEDECIMIENTO SUPERFICIAL (MÉTODO DE ROCÍO)
              </p>
              <div className="mt-1 mb-2">
                <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-primary/10 text-primary">Método: MET-7.2-17</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={handleBackFlush}>
              Cancelar
            </Button>
            {/* Botones de acciones en modo view y no aceptado SOLO PARA ADMIN */}
            {mode === 'view' && !editMode && estatus !== 'aceptado' && estatus !== 'rechazado' && puedeRevisar && (
              <>
                <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
                <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
                <Button variant="secondary" onClick={handleEditar}>Editar</Button>
              </>
            )}
            {/* Botón guardar edición SOLO PARA ADMIN */}
            {mode === 'view' && editMode && estatus !== 'aceptado' && puedeRevisar && (
              <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
            )}
            {/* Botón guardar en modo create */}
            {mode === 'create' && (
              <Button onClick={handleSave} disabled={guardando}>
                <Save className="h-4 w-4 mr-2" />
                {guardando ? 'Guardando...' : 'Guardar Formulario'}
              </Button>
            )}
          </div>
        </div>

        {/* Información del Pedido */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Información del Pedido</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Pedido No: {pedidoId}</p>
          </CardContent>
        </Card>

        {/* Datos de la Solicitud */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Datos de la Solicitud</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="numero_solicitud">Número de solicitud</Label>
                <Input
                  id="numero_solicitud"
                  type="text"
                  placeholder="Ej. 12345"
                  value={formData.numero_solicitud || ''}
                  onChange={e => handleInputChange('numero_solicitud', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="id_termohigrometro">ID de termohigrómetro</Label>
                <Select
                  value={formData.id_termohigrometro || ''}
                  onValueChange={value => handleInputChange('id_termohigrometro', value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Seleccione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="E-59">E-59</SelectItem>
                    <SelectItem value="E-57">E-57</SelectItem>
                    <SelectItem value="E-56">E-56</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Parámetros de Prueba */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Parámetros de Prueba</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left font-medium">FECHA:</th>
                    <th className="border border-border p-3 text-left font-medium">HORA:</th>
                    <th className="border border-border p-3 text-left font-medium">°C:</th>
                    <th className="border border-border p-3 text-left font-medium">% HR:</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-2">
                      <Input
                        type="date"
                        value={formData.fecha}
                        onChange={(e) => handleInputChange('fecha', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="time"
                        value={formData.hora}
                        onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="°C"
                        value={formData.gradosC}
                        onChange={(e) => handleInputChange('gradosC', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="%"
                        value={formData.porcentajeHR}
                        onChange={(e) => handleInputChange('porcentajeHR', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2" colSpan={4}>
                      <Label htmlFor="desv_metodo">Desviación al método</Label>
                      <Input
                        id="desv_metodo"
                        type="text"
                        placeholder="Describa desviación al método"
                        value={formData.desv_metodo || ""}
                        onChange={e => handleInputChange('desv_metodo', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Temperatura del Agua */}
            <div className="mt-4 overflow-x-auto">
              <table className="w-full border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left font-medium">
                      TEMPERATURA DEL AGUA:
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">
                      <div className="flex items-center gap-8">
                        <div className="flex items-center gap-2">
                          <span>20±2 °C (</span>
                          <Checkbox
                            checked={formData.temp20C}
                            onCheckedChange={(checked) => handleTemperatureChange('temp20C', checked as boolean)}
                            disabled={isReadOnly}
                          />
                          <span>)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>27±2 °C (</span>
                          <Checkbox
                            checked={formData.temp27C}
                            onCheckedChange={(checked) => handleTemperatureChange('temp27C', checked as boolean)}
                            disabled={isReadOnly}
                          />
                          <span>)</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Datos y Firmas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tabla de Grado de Humedecimiento */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-primary">Resultados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border border-border">
                    <thead>
                      <tr className="bg-muted">
                        <th className="border border-border p-3 text-center font-medium">NO.</th>
                        <th className="border border-border p-3 text-center font-medium">
                          GRADO DE<br />HUMEDECIMIENTO
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3].map((num) => (
                        <tr key={num}>
                          <td className="border border-border p-3 text-center font-medium bg-muted">
                            {num}
                          </td>
                          <td className="border border-border p-2">
                            <Select
                              value={formData[`humed${num}` as keyof NMXA4920FormData] as string}
                              onValueChange={value => handleInputChange(`humed${num}` as keyof NMXA4920FormData, value)}
                              disabled={isReadOnly}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Selecciona una opción..." />
                              </SelectTrigger>
                              <SelectContent>
                                {HUMED_OPTIONS.map((opt, idx) => (
                                  <SelectItem key={idx} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Firmas */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-primary">Firmas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="realizo">REALIZÓ:</Label>
                  <Input
                    id="realizo"
                    type="text"
                    placeholder="Nombre de quien realizó"
                    value={formData.realizo}
                    readOnly
                    className="mt-2 bg-muted-foreground/10 cursor-not-allowed"
                  />
                </div>
                <div>
                  <Label htmlFor="superviso">SUPERVISÓ:</Label>
                  <Input
                    id="superviso"
                    type="text"
                    placeholder="Nombre de quien supervisó"
                    value={formData.superviso}
                    onChange={(e) => handleInputChange('superviso', e.target.value)}
                    disabled
                    className="mt-2"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Observaciones */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-primary">Observaciones</CardTitle>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Mostrar en Reporte Cliente
              <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
            </span>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="observaciones">OBSERVACIONES:</Label>
              <Textarea
                id="observaciones"
                placeholder="Ingrese las observaciones aquí..."
                value={formData.observaciones}
                onChange={(e) => handleInputChange('observaciones', e.target.value)}
                disabled={isReadOnly}
                className="mt-2 min-h-[100px]"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}